import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';
import LoadingScreen from '../common/LoadingScreen';
import VerificationSuccessMessage from './VerificationSuccessMessage';

/**
 * Component to handle redirection after email verification
 * This component checks if the user's email is verified and redirects them accordingly
 */
const EmailVerificationRedirect: React.FC = () => {
  const { currentUser, isEmailVerified, loading } = useAuth();
  const [checkingStatus, setCheckingStatus] = useState<boolean>(true);
  const [verificationSuccess, setVerificationSuccess] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkVerification = async () => {
      if (!currentUser || loading) return;
        try {
        // Force a reload to get the latest verification status
        await currentUser.reload();
        
        logger.info(`📧 Email verification status check: ${isEmailVerified ? 'Verified' : 'Not verified'}`);
          if (currentUser.emailVerified) {
          // Email is verified, show success message
          logger.info(`✅ Email verified successfully`);
          
          // Update localStorage to notify other open tabs about verification
          try {
            localStorage.setItem('emailVerificationComplete', Date.now().toString());
            localStorage.removeItem('verificationEmailSent'); // Clear the verification email timestamp
            logger.info('🔄 Updated localStorage with verification status for cross-tab communication');
          } catch (err) {
            logger.error('❌ Error updating localStorage after verification', err);
          }
          
          setVerificationSuccess(true);
        } else {
          // Email is not verified, redirect to profile page with a message
          logger.info('❌ Email not verified, redirecting to profile page');
          navigate('/my-profile', { 
            replace: true, 
            state: { verificationFailed: true } 
          });
        }
      } catch (error) {
        logger.error('❌ Error checking email verification status', error);
        // On error, redirect to profile page
        navigate('/my-profile', { replace: true });
      } finally {
        setCheckingStatus(false);
      }
    };
    
    checkVerification();
  }, [currentUser, isEmailVerified, loading, navigate, location.state]);
  
  if (checkingStatus || loading) {
    return <LoadingScreen message="Checking verification status..." />;
  }
  
  if (verificationSuccess) {
    return <VerificationSuccessMessage redirectTo={location.state?.from?.pathname || '/my-profile'} />;
  }
  
  // This should not be displayed as we always redirect or show success message
  return <LoadingScreen message="Redirecting..." />;
};

export default EmailVerificationRedirect;
